/**
 * Salary Advance sub-page (Talent → Salary Advance).
 *
 * Lists all salary advance loans issued to in-house lessee riders and lets
 * authorised users:
 *   • Add a new advance (with rider search dropdown, e-sign pad, PDF receipt).
 *   • Edit / Delete / View existing entries (subject to RBAC).
 *
 * The advance is recovered automatically by the payroll wizard — this page
 * is only for issuing and tracking. Status badges reflect the recovery
 * state set by the payroll approval flow.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Eye,
  Pencil,
  Trash2,
  MoreHorizontal,
  Plus,
  Search,
  FileDown,
  RefreshCw,
  Loader2,
  X,
  Pen,
} from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { formatErrorMessage } from '../utils/formatError';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// ---------------------------------------------------------------------------
// Signature pad — small canvas drawing component, returns a PNG data URL.
// ---------------------------------------------------------------------------
function SignaturePad({ value, onChange, height = 140 }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const [empty, setEmpty] = useState(!value);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    // Fill with white background so the PNG is opaque (looks clean on PDF)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      img.src = value;
    }
  }, [value]);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = getPoint(e);
    setEmpty(false);
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const p = getPoint(e);
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
  };
  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const canvas = canvasRef.current;
    onChange?.(canvas.toDataURL('image/png'));
  };
  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setEmpty(true);
    onChange?.('');
  };

  return (
    <div className="space-y-1">
      <div className="relative border-2 border-dashed border-slate-300 rounded-md bg-white" style={{ height }}>
        <canvas
          ref={canvasRef}
          width={600}
          height={height * 2}
          style={{ width: '100%', height: '100%', touchAction: 'none', cursor: 'crosshair' }}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          data-testid="signature-canvas"
        />
        {empty && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-slate-400 text-sm gap-2">
            <Pen className="w-4 h-4" /> Sign here
          </div>
        )}
        <button
          type="button"
          onClick={clear}
          className="absolute top-1 right-1 bg-white/90 hover:bg-white text-slate-500 hover:text-slate-700 border rounded px-2 py-0.5 text-xs"
          data-testid="signature-clear"
        >
          Clear
        </button>
      </div>
      <p className="text-xs text-slate-500">
        The rider should sign with their finger (mobile) or trackpad/mouse.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Searchable rider dropdown — fetched from the in-house lessee endpoint.
// ---------------------------------------------------------------------------
function RiderSearchSelect({ riders, value, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return riders.slice(0, 100);
    return riders
      .filter(
        (r) =>
          (r.name || '').toLowerCase().includes(q) ||
          (r.emirates_id || '').toLowerCase().includes(q) ||
          (r.id || '').toLowerCase().includes(q) ||
          (r.platform_id || '').toLowerCase().includes(q),
      )
      .slice(0, 100);
  }, [query, riders]);

  return (
    <div className="relative">
      <div className="flex items-center border rounded-md bg-white">
        <Search className="w-4 h-4 ml-2 text-slate-400" />
        <Input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Search rider by name, Emirates ID, or Rider ID…"
          className="border-0 focus-visible:ring-0"
          data-testid="rider-search-input"
        />
      </div>
      {open && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 max-h-72 overflow-auto bg-white border rounded-md shadow-lg"
          onMouseLeave={() => setOpen(false)}
        >
          {filtered.length === 0 ? (
            <div className="px-3 py-4 text-sm text-slate-500 text-center">No riders match</div>
          ) : (
            filtered.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => {
                  onChange(r);
                  setQuery(r.name);
                  setOpen(false);
                }}
                className={`w-full text-left px-3 py-2 hover:bg-amber-50 text-sm border-b last:border-b-0 ${
                  value?.id === r.id ? 'bg-amber-50' : ''
                }`}
                data-testid={`rider-option-${r.id}`}
              >
                <div className="font-semibold text-slate-800">{r.name}</div>
                <div className="text-xs text-slate-500">
                  {r.emirates_id || '-'} · {r.platform || '-'}
                  {r.assigned_bike_plate ? ` · ${r.assigned_bike_plate}` : ''}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const STATUS_BADGE = {
  pending_recovery: { label: 'Pending Recovery', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  partially_recovered: { label: 'Partially Recovered', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  recovered: { label: 'Recovered', cls: 'bg-green-100 text-green-700 border-green-200' },
  cancelled: { label: 'Cancelled', cls: 'bg-slate-200 text-slate-600 border-slate-300' },
};

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------
const SalaryAdvance = ({ user, canCreate = true, canEdit = true, canDelete = true }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [activeRecord, setActiveRecord] = useState(null);
  const [riders, setRiders] = useState([]);
  const [ridersLoading, setRidersLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const today = new Date().toISOString().slice(0, 10);
  const initialForm = { rider: null, amount: '', advance_date: today, notes: '', signature_data_url: '' };
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);

  // -----------------------------------------------------------------------
  // Data fetching
  // -----------------------------------------------------------------------
  const fetchRecords = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      const res = await axios.get(`${API}/api/salary-advance?${params.toString()}`, {
        headers: authHeader(),
      });
      setRecords(res.data?.salary_advances || []);
    } catch (err) {
      toast.error(formatErrorMessage(err, 'Failed to load salary advances'));
    } finally {
      setLoading(false);
    }
  };

  const fetchRiders = async () => {
    try {
      setRidersLoading(true);
      const res = await axios.get(`${API}/api/salary-advance/eligible-riders`, {
        headers: authHeader(),
      });
      setRiders(res.data?.riders || []);
    } catch (err) {
      toast.error(formatErrorMessage(err, 'Failed to load in-house riders'));
    } finally {
      setRidersLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  // We intentionally re-run only when filters change.
  // eslint-disable-next-line
  }, [statusFilter]);

  // -----------------------------------------------------------------------
  // Save / Update
  // -----------------------------------------------------------------------
  const handleSave = async () => {
    if (!form.rider) {
      toast.error('Please select a rider');
      return;
    }
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) {
      toast.error('Enter a valid advance amount');
      return;
    }
    if (!form.signature_data_url) {
      toast.error('Rider signature is required');
      return;
    }
    try {
      setSaving(true);
      const res = await axios.post(
        `${API}/api/salary-advance`,
        {
          rider_id: form.rider.id,
          amount: amt,
          advance_date: form.advance_date,
          notes: form.notes,
          signature_data_url: form.signature_data_url,
        },
        { headers: authHeader() },
      );
      toast.success(`Salary advance ${res.data?.salary_advance?.transaction_id} created`);
      setAddOpen(false);
      setForm(initialForm);
      fetchRecords();
    } catch (err) {
      toast.error(formatErrorMessage(err, 'Failed to create salary advance'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!activeRecord) return;
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) {
      toast.error('Enter a valid advance amount');
      return;
    }
    try {
      setSaving(true);
      await axios.put(
        `${API}/api/salary-advance/${activeRecord.id}`,
        {
          amount: amt,
          advance_date: form.advance_date,
          notes: form.notes,
          signature_data_url: form.signature_data_url || undefined,
        },
        { headers: authHeader() },
      );
      toast.success('Salary advance updated');
      setEditOpen(false);
      setActiveRecord(null);
      fetchRecords();
    } catch (err) {
      toast.error(formatErrorMessage(err, 'Failed to update advance'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rec) => {
    if (!window.confirm(`Delete ${rec.transaction_id} (${rec.rider_name})?`)) return;
    try {
      await axios.delete(`${API}/api/salary-advance/${rec.id}`, { headers: authHeader() });
      toast.success('Salary advance deleted');
      fetchRecords();
    } catch (err) {
      toast.error(formatErrorMessage(err, 'Failed to delete advance'));
    }
  };

  const downloadReceipt = async (rec) => {
    try {
      setDownloadingId(rec.id);
      const res = await axios.get(`${API}/api/salary-advance/${rec.id}/receipt`, {
        headers: authHeader(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SalaryAdvance_${rec.transaction_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatErrorMessage(err, 'Failed to download receipt'));
    } finally {
      setDownloadingId(null);
    }
  };

  const openAdd = async () => {
    setForm(initialForm);
    setAddOpen(true);
    if (riders.length === 0) await fetchRiders();
  };

  const openEdit = (rec) => {
    setActiveRecord(rec);
    setForm({
      rider: { ...rec, id: rec.rider_id, name: rec.rider_name },
      amount: String(rec.amount || ''),
      advance_date: rec.advance_date || today,
      notes: rec.notes || '',
      signature_data_url: rec.signature_data_url || '',
    });
    setEditOpen(true);
  };

  const openView = async (rec) => {
    setActiveRecord(rec);
    setViewOpen(true);
    // Fetch the enriched record so the recovery history includes batch context
    // (platform, exact recovered_at, approved_by, batch link).
    try {
      const res = await axios.get(`${API}/api/salary-advance/${rec.id}`, {
        headers: authHeader(),
      });
      if (res.data) setActiveRecord(res.data);
    } catch (_e) { /* keep the row snapshot */ }
  };

  // -----------------------------------------------------------------------
  // Filtered view (client-side search across already-loaded records)
  // -----------------------------------------------------------------------
  const visibleRecords = useMemo(() => {
    if (!search) return records;
    const q = search.toLowerCase();
    return records.filter(
      (r) =>
        (r.transaction_id || '').toLowerCase().includes(q) ||
        (r.rider_name || '').toLowerCase().includes(q) ||
        (r.rider_emirates_id || '').toLowerCase().includes(q) ||
        (r.rider_unique_id || '').toLowerCase().includes(q),
    );
  }, [records, search]);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <div className="space-y-4" data-testid="salary-advance-page">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-2">
          <div>
            <CardTitle className="text-xl">Salary Advance</CardTitle>
            <CardDescription>
              Issue salary advances to in-house lessee riders. Recovery is enforced on the next payroll run.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchRecords} disabled={loading} data-testid="sa-refresh">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
            {canCreate && (
              <Button onClick={openAdd} className="gap-2 bg-amber-600 hover:bg-amber-700" data-testid="sa-add-btn">
                <Plus className="w-4 h-4" /> Add Salary Advance
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Filters */}
          <div className="flex flex-wrap gap-2 items-center">
            <div className="flex items-center border rounded-md bg-white flex-1 min-w-[220px] max-w-md">
              <Search className="w-4 h-4 ml-2 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search Txn ID / Rider name / Emirates ID"
                className="border-0 focus-visible:ring-0"
                data-testid="sa-search"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border rounded-md px-2 py-2 text-sm bg-white"
              data-testid="sa-status-filter"
            >
              <option value="">All statuses</option>
              <option value="pending_recovery">Pending Recovery</option>
              <option value="partially_recovered">Partially Recovered</option>
              <option value="recovered">Recovered</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <div className="text-xs text-slate-500 ml-auto">
              {visibleRecords.length} record{visibleRecords.length === 1 ? '' : 's'}
            </div>
          </div>

          {/* Log table */}
          <div className="border rounded-md overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-amber-50">
                  <TableHead className="whitespace-nowrap">Transaction ID</TableHead>
                  <TableHead className="whitespace-nowrap">Rider ID</TableHead>
                  <TableHead className="whitespace-nowrap">Platform</TableHead>
                  <TableHead className="whitespace-nowrap">Platform ID</TableHead>
                  <TableHead className="whitespace-nowrap">Rider Name</TableHead>
                  <TableHead className="whitespace-nowrap">Transaction Date</TableHead>
                  <TableHead className="whitespace-normal max-w-[150px]">Amount in AED</TableHead>
                  <TableHead className="whitespace-nowrap">Status</TableHead>
                  <TableHead className="whitespace-nowrap text-center">Receipt</TableHead>
                  <TableHead className="whitespace-nowrap text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-slate-500 py-6">
                      <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                    </TableCell>
                  </TableRow>
                ) : visibleRecords.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-slate-500 py-6">
                      No salary advances yet. Click <strong>Add Salary Advance</strong> to issue one.
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleRecords.map((rec) => {
                    const badge = STATUS_BADGE[rec.status] || STATUS_BADGE.pending_recovery;
                    return (
                      <TableRow key={rec.id} data-testid={`sa-row-${rec.id}`}>
                        <TableCell className="font-mono text-xs">{rec.transaction_id}</TableCell>
                        <TableCell className="font-mono text-xs">{rec.rider_unique_id || '-'}</TableCell>
                        <TableCell className="text-sm">{rec.rider_platform || '-'}</TableCell>
                        <TableCell className="font-mono text-xs">{rec.rider_platform_id || '-'}</TableCell>
                        <TableCell>
                          <div className="font-medium">{rec.rider_name}</div>
                          <div className="text-xs text-slate-500">{rec.rider_emirates_id || '-'}</div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm">{rec.advance_date}</TableCell>
                        <TableCell className="whitespace-normal break-words max-w-[140px] font-semibold text-amber-700">
                          AED {Number(rec.amount || 0).toFixed(2)}
                          {Number(rec.recovered_amount || 0) > 0 && (
                            <div className="text-[10px] font-normal text-slate-500 whitespace-normal">
                              Recovered: AED {Number(rec.recovered_amount || 0).toFixed(2)}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-[10px] ${badge.cls}`}>
                            {badge.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={() => downloadReceipt(rec)}
                            disabled={downloadingId === rec.id}
                            data-testid={`sa-download-${rec.id}`}
                            title="Download receipt PDF"
                          >
                            {downloadingId === rec.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <FileDown className="w-4 h-4 text-amber-700" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`sa-actions-${rec.id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openView(rec)} data-testid={`sa-view-${rec.id}`}>
                                <Eye className="w-4 h-4 mr-2" /> View details
                              </DropdownMenuItem>
                              {canEdit && (
                                <DropdownMenuItem
                                  onClick={() => openEdit(rec)}
                                  disabled={rec.status === 'recovered' || rec.status === 'cancelled'}
                                  data-testid={`sa-edit-${rec.id}`}
                                >
                                  <Pencil className="w-4 h-4 mr-2" /> Edit
                                </DropdownMenuItem>
                              )}
                              {canDelete && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => handleDelete(rec)}
                                    disabled={rec.status === 'recovered'}
                                    className="text-red-600 focus:text-red-700"
                                    data-testid={`sa-delete-${rec.id}`}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* ---------------------------------------------------------------- */}
      {/* ADD DIALOG                                                       */}
      {/* ---------------------------------------------------------------- */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sa-add-dialog">
          <DialogHeader>
            <DialogTitle>Add Salary Advance</DialogTitle>
            <DialogDescription>
              Issue a salary advance to an in-house lessee rider. A signed receipt PDF will be generated and the
              amount will be automatically deducted on the rider&apos;s next monthly payroll.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold">Rider</Label>
              {ridersLoading ? (
                <div className="text-sm text-slate-500 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading riders…
                </div>
              ) : (
                <RiderSearchSelect
                  riders={riders}
                  value={form.rider}
                  onChange={(rider) => setForm((f) => ({ ...f, rider }))}
                />
              )}
            </div>

            {form.rider && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs" data-testid="sa-rider-snapshot">
                <div>
                  <span className="text-slate-500">Rider ID:</span>{' '}
                  <span className="font-mono">{form.rider.id}</span>
                </div>
                <div>
                  <span className="text-slate-500">Emirates ID:</span>{' '}
                  <span className="font-mono">{form.rider.emirates_id || '-'}</span>
                </div>
                <div>
                  <span className="text-slate-500">Platform:</span>{' '}
                  <span className="font-semibold">{form.rider.platform || '-'}</span>
                </div>
                <div>
                  <span className="text-slate-500">Bike Plate:</span>{' '}
                  <span className="font-mono">{form.rider.assigned_bike_plate || '-'}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-slate-500">Lessee:</span>{' '}
                  <span className="font-semibold">{form.rider.lessee_name || '-'}</span>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Advance Amount (AED)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.amount}
                  onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                  placeholder="e.g. 500"
                  data-testid="sa-amount-input"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">Advance Date</Label>
                <Input
                  type="date"
                  value={form.advance_date}
                  onChange={(e) => setForm((f) => ({ ...f, advance_date: e.target.value }))}
                  data-testid="sa-date-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold">Notes (optional)</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Reason / context for this advance"
                data-testid="sa-notes-input"
              />
            </div>

            <div>
              <Label className="text-xs font-semibold">Rider Signature *</Label>
              <SignaturePad
                value={form.signature_data_url}
                onChange={(v) => setForm((f) => ({ ...f, signature_data_url: v }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} data-testid="sa-cancel-btn">
              Cancel
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              onClick={handleSave}
              disabled={saving}
              data-testid="sa-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {saving ? 'Saving…' : 'Save Advance & Generate Receipt'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------------------------------------------------------- */}
      {/* EDIT DIALOG                                                      */}
      {/* ---------------------------------------------------------------- */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="sa-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Salary Advance</DialogTitle>
            <DialogDescription>
              {activeRecord?.transaction_id} — {activeRecord?.rider_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Advance Amount (AED)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                  data-testid="sa-edit-amount-input"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">Advance Date</Label>
                <Input
                  type="date"
                  value={form.advance_date}
                  onChange={(e) => setForm((f) => ({ ...f, advance_date: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold">Notes</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>
            {Number(activeRecord?.recovered_amount || 0) > 0 && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                AED {Number(activeRecord.recovered_amount).toFixed(2)} already recovered. New amount must be greater
                than the recovered total.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={handleUpdate} disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------------------------------------------------------- */}
      {/* VIEW DIALOG                                                      */}
      {/* ---------------------------------------------------------------- */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-lg" data-testid="sa-view-dialog">
          <DialogHeader>
            <DialogTitle>Salary Advance Details</DialogTitle>
          </DialogHeader>
          {activeRecord && (
            <div className="space-y-2 text-sm">
              <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                <div className="text-slate-500">Transaction ID</div>
                <div className="font-mono">{activeRecord.transaction_id}</div>
                <div className="text-slate-500">Rider Name</div>
                <div className="font-semibold">{activeRecord.rider_name}</div>
                <div className="text-slate-500">Rider ID</div>
                <div className="font-mono">{activeRecord.rider_unique_id}</div>
                <div className="text-slate-500">Emirates ID</div>
                <div className="font-mono">{activeRecord.rider_emirates_id || '-'}</div>
                <div className="text-slate-500">Platform</div>
                <div>{activeRecord.rider_platform || '-'}</div>
                <div className="text-slate-500">Platform ID</div>
                <div className="font-mono">{activeRecord.rider_platform_id || '-'}</div>
                <div className="text-slate-500">Bike Plate</div>
                <div>{activeRecord.rider_bike_plate || '-'}</div>
                <div className="text-slate-500">Lessee</div>
                <div>{activeRecord.lessee_name}</div>
                <div className="text-slate-500">Date</div>
                <div>{activeRecord.advance_date}</div>
                <div className="text-slate-500">Amount</div>
                <div className="font-bold text-amber-700">AED {Number(activeRecord.amount || 0).toFixed(2)}</div>
                <div className="text-slate-500">Recovered</div>
                <div>AED {Number(activeRecord.recovered_amount || 0).toFixed(2)}</div>
                <div className="text-slate-500">Outstanding</div>
                <div className="font-semibold">
                  AED {Number(activeRecord.outstanding_amount || 0).toFixed(2)}
                </div>
                <div className="text-slate-500">Status</div>
                <div>
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${
                      (STATUS_BADGE[activeRecord.status] || STATUS_BADGE.pending_recovery).cls
                    }`}
                  >
                    {(STATUS_BADGE[activeRecord.status] || STATUS_BADGE.pending_recovery).label}
                  </Badge>
                </div>
              </div>

              {(activeRecord.recovery_history || []).length > 0 && (
                <div className="pt-3 border-t mt-3">
                  <p className="text-xs font-semibold text-slate-600 mb-2">Recovery History</p>
                  <ul className="space-y-2 text-xs">
                    {activeRecord.recovery_history.map((h, i) => {
                      const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                      const monthLabel = h.batch_month
                        ? `${monthNames[Number(h.batch_month)] || h.batch_month} ${h.batch_year}`
                        : `${h.batch_year || ''}`;
                      const recAt = h.recovered_at ? new Date(h.recovered_at).toLocaleString() : '';
                      return (
                        <li key={i} className="bg-emerald-50 border border-emerald-200 rounded p-2">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="font-semibold text-emerald-900" data-testid={`sa-recovery-month-${i}`}>
                                {h.platform ? `${h.platform} · ` : ''}{monthLabel} Payroll
                              </div>
                              {recAt && (
                                <div className="text-slate-500 text-[10px]">Recovered on {recAt}</div>
                              )}
                              {h.submitted_by && (
                                <div className="text-slate-500 text-[10px]">Submitted by {h.submitted_by}</div>
                              )}
                              {h.approved_by && (
                                <div className="text-slate-500 text-[10px]">Approved by {h.approved_by}{h.approved_at ? ` on ${new Date(h.approved_at).toLocaleDateString()}` : ''}</div>
                              )}
                              {h.batch_status && (
                                <div className="text-slate-500 text-[10px]">Batch status: <span className="font-mono">{h.batch_status}</span></div>
                              )}
                            </div>
                            <span className="font-mono font-semibold text-emerald-800 whitespace-nowrap ml-2">
                              AED {Number(h.amount_recovered || 0).toFixed(2)}
                            </span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {activeRecord.signature_data_url && (
                <div className="pt-3 border-t mt-3">
                  <p className="text-xs font-semibold text-slate-600 mb-1">Rider Signature</p>
                  <img
                    src={activeRecord.signature_data_url}
                    alt="signature"
                    className="border rounded bg-white max-h-32"
                  />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => activeRecord && downloadReceipt(activeRecord)}
              className="gap-2"
            >
              <FileDown className="w-4 h-4" /> Receipt PDF
            </Button>
            <Button onClick={() => setViewOpen(false)}>
              <X className="w-4 h-4 mr-1" /> Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalaryAdvance;
